import { apiService } from './api';

interface GameStartParams {
  gameId: string;
  sessionId: string;
  pageLoadTimeMs: number;
  bundleVersion?: number;
  windowWidth: number;
  windowHeight: number;
  gameLayout: string;
  iframeLoadTimeMs?: number;
  customMetrics?: Record<string, any>;
}

interface GameMetricsUpdate {
  totalPlayTimeMs?: number;
  totalPauseTimeMs?: number;
  visibilityChanges?: number;
  consoleErrors?: any[];
  consoleWarnings?: any[];
  customMetrics?: Record<string, any>;
}

/**
 * Webplay Game Log Service
 * Handles game session tracking for webplay with throttled updates
 */
class WebplayGameLogService {
  private currentGameLogId: string | null = null;
  private updateThrottleMs: number = 30000; // 30 seconds
  private lastUpdateTime: number = 0;
  private metricsBuffer: GameMetricsUpdate[] = [];
  private accumulatedMetrics: GameMetricsUpdate = {};
  
  // Time tracking
  private gameStartTime: number = 0;
  private totalPlayTime: number = 0;
  private totalPauseTime: number = 0;
  private lastTrackingTime: number = 0;
  private isVisible: boolean = true;
  private visibilityChangeCount: number = 0;

  /**
   * Start a new game session
   */
  async startGame(params: GameStartParams): Promise<string> {
    try {
      console.log('🎮 [WebplayGameLog] Starting game session:', {
        gameId: params.gameId,
        sessionId: params.sessionId
      });

      if (!params.sessionId) {
        console.error('❌ [WebplayGameLog] Cannot start game without sessionId');
        throw new Error('Session ID is required for game logging');
      }

      // Skip game logging for temporary offline sessions (they can't be linked to server data)
      if (params.sessionId.startsWith('temp-')) {
        console.log('📊 [WebplayGameLog] Skipping game logging for temporary offline session');
        throw new Error('Cannot log games for temporary offline sessions');
      }

      const response = await apiService.post('/webplay/game/started', params);

      if (response.data.success && response.data.data?.gameLogId) {
        this.currentGameLogId = response.data.data.gameLogId;
        this.gameStartTime = Date.now();
        this.lastTrackingTime = Date.now();
        this.lastUpdateTime = Date.now();
        this.totalPlayTime = 0;
        this.totalPauseTime = 0;
        this.visibilityChangeCount = 0;
        this.isVisible = true;
        this.metricsBuffer = [];
        this.accumulatedMetrics = {};

        console.log('✅ [WebplayGameLog] Game session started:', this.currentGameLogId);
        return this.currentGameLogId;
      } else {
        throw new Error('Invalid response from game start endpoint');
      }
    } catch (error) {
      console.error('❌ [WebplayGameLog] Failed to start game session:', error);
      throw error;
    }
  }

  /**
   * Update game metrics (throttled)
   */
  async updateMetrics(metrics: GameMetricsUpdate): Promise<void> {
    if (!this.currentGameLogId) {
      console.warn('⚠️ [WebplayGameLog] No active game session for metrics update');
      return;
    }

    // Update accumulated metrics
    this.updateAccumulatedMetrics(metrics);

    // Throttle updates
    const now = Date.now();
    if (now - this.lastUpdateTime < this.updateThrottleMs) {
      this.metricsBuffer.push(metrics);
      return;
    }

    try {
      const combinedMetrics = this.combineBufferedMetrics(metrics);
      
      console.log('📊 [WebplayGameLog] Updating game metrics:', {
        gameLogId: this.currentGameLogId,
        playTimeMs: combinedMetrics.totalPlayTimeMs,
        pauseTimeMs: combinedMetrics.totalPauseTimeMs
      });

      await apiService.post('/webplay/game/update', {
        gameLogId: this.currentGameLogId,
        ...combinedMetrics
      });

      this.lastUpdateTime = now;
      this.metricsBuffer = [];
      
      // Clear console logs from accumulated (they've been sent)
      delete this.accumulatedMetrics.consoleWarns;
      delete this.accumulatedMetrics.consoleErrors;

      console.log('✅ [WebplayGameLog] Metrics updated successfully');
    } catch (error) {
      console.error('❌ [WebplayGameLog] Failed to update metrics:', error);
    }
  }

  /**
   * Force immediate metrics update (bypasses throttling)
   */
  async forceUpdate(): Promise<void> {
    if (!this.currentGameLogId) {
      return;
    }

    try {
      console.log('🔄 [WebplayGameLog] Forcing immediate metrics update...');
      
      const combinedMetrics = this.combineBufferedMetrics({});
      
      if (Object.keys(combinedMetrics).length === 0) {
        console.log('⚠️ [WebplayGameLog] No metrics to update');
        return;
      }

      await apiService.post('/webplay/game/update', {
        gameLogId: this.currentGameLogId,
        ...combinedMetrics
      });

      this.lastUpdateTime = Date.now();
      this.metricsBuffer = [];
      
      delete this.accumulatedMetrics.consoleWarns;
      delete this.accumulatedMetrics.consoleErrors;

      console.log('✅ [WebplayGameLog] Forced update completed');
    } catch (error) {
      console.error('❌ [WebplayGameLog] Failed to force update:', error);
    }
  }

  /**
   * End the current game session
   * @param useBeacon - Use navigator.sendBeacon for guaranteed delivery (for page unload)
   */
  async endGame(useBeacon: boolean = false): Promise<void> {
    if (!this.currentGameLogId) {
      console.warn('⚠️ [WebplayGameLog] No active game session to end');
      return;
    }

    try {
      console.log('🏁 [WebplayGameLog] Ending game session:', this.currentGameLogId);

      const gameLogId = this.currentGameLogId;

      // Force final metrics update before ending (skip if using beacon)
      if (!useBeacon) {
        await this.forceUpdate();
      }

      // Use sendBeacon for page unload to ensure delivery
      if (useBeacon && navigator.sendBeacon) {
        const url = `${import.meta.env.VITE_API_BASE_URL}/api/webplay/game/ended`;
        const data = JSON.stringify({ gameLogId });
        const blob = new Blob([data], { type: 'application/json' });
        navigator.sendBeacon(url, blob);
        console.log('✅ [WebplayGameLog] Game end beacon sent');
      } else {
        await apiService.post('/webplay/game/ended', {
          gameLogId
        });
        console.log('✅ [WebplayGameLog] Game session ended');
      }
      
      this.currentGameLogId = null;
      this.metricsBuffer = [];
      this.accumulatedMetrics = {};
    } catch (error) {
      console.error('❌ [WebplayGameLog] Failed to end game session:', error);
      this.currentGameLogId = null;
      this.metricsBuffer = [];
      this.accumulatedMetrics = {};
    }
  }

  /**
   * Track visibility change
   */
  trackVisibilityChange(isVisible: boolean): void {
    const now = Date.now();
    
    if (this.isVisible && !isVisible) {
      // Going hidden - accumulate play time
      this.totalPlayTime += (now - this.lastTrackingTime);
    } else if (!this.isVisible && isVisible) {
      // Coming back visible - accumulate pause time
      this.totalPauseTime += (now - this.lastTrackingTime);
    }
    
    this.isVisible = isVisible;
    this.lastTrackingTime = now;
    this.visibilityChangeCount++;
    
    // Send metrics update on visibility change
    this.updateMetrics({
      totalPlayTimeMs: this.totalPlayTime,
      totalPauseTimeMs: this.totalPauseTime,
      visibilityChanges: this.visibilityChangeCount
    });
  }

  /**
   * Add console error
   */
  addConsoleError(error: any): void {
    if (!this.currentGameLogId) return;

    if (!this.accumulatedMetrics.consoleErrors) {
      this.accumulatedMetrics.consoleErrors = [];
    }
    this.accumulatedMetrics.consoleErrors.push({
      message: error.message || String(error),
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Add console warning
   */
  addConsoleWarning(warning: any): void {
    if (!this.currentGameLogId) return;

    if (!this.accumulatedMetrics.consoleWarnings) {
      this.accumulatedMetrics.consoleWarnings = [];
    }
    this.accumulatedMetrics.consoleWarnings.push({
      message: warning.message || String(warning),
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Get current game log ID
   */
  getCurrentGameLogId(): string | null {
    return this.currentGameLogId;
  }

  /**
   * Get total play time
   */
  getTotalPlayTime(): number {
    if (this.isVisible) {
      return this.totalPlayTime + (Date.now() - this.lastTrackingTime);
    }
    return this.totalPlayTime;
  }

  /**
   * Get total pause time
   */
  getTotalPauseTime(): number {
    if (!this.isVisible) {
      return this.totalPauseTime + (Date.now() - this.lastTrackingTime);
    }
    return this.totalPauseTime;
  }

  /**
   * Check if there's an active game session
   */
  hasActiveSession(): boolean {
    return this.currentGameLogId !== null;
  }

  /**
   * Update accumulated metrics
   */
  private updateAccumulatedMetrics(metrics: GameMetricsUpdate): void {
    if (metrics.totalPlayTimeMs !== undefined) {
      this.accumulatedMetrics.totalPlayTimeMs = metrics.totalPlayTimeMs;
    }
    if (metrics.totalPauseTimeMs !== undefined) {
      this.accumulatedMetrics.totalPauseTimeMs = metrics.totalPauseTimeMs;
    }
    if (metrics.visibilityChanges !== undefined) {
      this.accumulatedMetrics.visibilityChanges = metrics.visibilityChanges;
    }
  }

  /**
   * Combine buffered metrics with current metrics
   */
  private combineBufferedMetrics(currentMetrics: GameMetricsUpdate): GameMetricsUpdate {
    const combined: GameMetricsUpdate = { ...this.accumulatedMetrics };

    // Combine console errors
    const allErrors: any[] = [];
    this.metricsBuffer.forEach(m => {
      if (m.consoleErrors) allErrors.push(...m.consoleErrors);
    });
    if (currentMetrics.consoleErrors) allErrors.push(...currentMetrics.consoleErrors);
    if (this.accumulatedMetrics.consoleErrors) allErrors.push(...this.accumulatedMetrics.consoleErrors);
    if (allErrors.length > 0) {
      combined.consoleErrors = allErrors;
    }

    // Combine console warnings
    const allWarnings: any[] = [];
    this.metricsBuffer.forEach(m => {
      if (m.consoleWarnings) allWarnings.push(...m.consoleWarnings);
    });
    if (currentMetrics.consoleWarnings) allWarnings.push(...currentMetrics.consoleWarnings);
    if (this.accumulatedMetrics.consoleWarnings) allWarnings.push(...this.accumulatedMetrics.consoleWarnings);
    if (allWarnings.length > 0) {
      combined.consoleWarnings = allWarnings;
    }

    // Merge custom metrics
    if (currentMetrics.customMetrics || this.accumulatedMetrics.customMetrics) {
      combined.customMetrics = {
        ...this.accumulatedMetrics.customMetrics,
        ...currentMetrics.customMetrics
      };
    }

    return combined;
  }
}

// Export singleton instance
export const webplayGameLogService = new WebplayGameLogService();
export default webplayGameLogService;
